import type { ShelfBook } from '../shelves/model';
import { statusName } from '../book/model';
import { copyText, titleKey } from '../queue/model';

// The author and series pages (Discover, Part 2, v1.0; boards 13, 13p, 17 and 18, agreed with
// Kristi Nov 3): only the books on your list. An author's books fold into one group per series,
// then one per literary genre ("Standalone novels", "Nonfiction", "Poetry"). A series page shows
// the next book for you, any books out of series order in Future reads, the books in series
// order, and the numbers missing from your list. The full pages (bibliographies) come in Part 4.

export interface PageBook extends ShelfBook {
  /** Formats of copies you have now (the Copy column, as on the Queue). */
  formats: string[];
  library: string[];
  /** The book's series on this page (the series page's own; on an author's page, its first). */
  seriesId: string | null;
  seriesName: string | null;
  seriesPos: number | null;
  seriesLabel: string | null;
  /** The year it was first published. */
  published: number | null;
  /** Its place on Up Next or Future reads (#1 is the top). */
  rank: number | null;
  /** Your read, in words ("Reading · 27%", "Finished Jun 2026 · ★ 3.5"). */
  read: string;
}

export interface BookGroup {
  key: string;
  kind: 'series' | 'literary';
  title: string;
  seriesId?: string;
  books: PageBook[];
}

/** A group of books that aren't in a series, by literary genre (Kristi, Nov 3). */
export const LITERARY_GROUPS: [string, string][] = [
  ['fiction', 'Standalone novels'],
  ['nonfiction', 'Nonfiction'],
  ['poetry', 'Poetry'],
  ['drama', 'Drama'],
  ['', 'Other books'],
];

const byYear = (a: PageBook, b: PageBook) => (a.published ?? 99999) - (b.published ?? 99999);
const inSeriesOrder = (a: PageBook, b: PageBook) =>
  (a.seriesPos ?? 99999) - (b.seriesPos ?? 99999) || byYear(a, b) || titleKey(a.title).localeCompare(titleKey(b.title));

/**
 * An author's books in groups: each series (in series order; the series whose first book came out
 * first comes first), then the books in no series by literary genre, newest first.
 */
export function authorGroups(books: PageBook[]): BookGroup[] {
  const series = new Map<string, BookGroup>();
  const other = new Map<string, PageBook[]>();
  for (const b of books) {
    if (b.seriesId) {
      const g = series.get(b.seriesId) ?? { key: `series:${b.seriesId}`, kind: 'series' as const, title: b.seriesName ?? 'Series', seriesId: b.seriesId, books: [] };
      g.books.push(b);
      series.set(b.seriesId, g);
    } else {
      const k = LITERARY_GROUPS.some(([g]) => g && g === b.literary) ? (b.literary as string) : '';
      other.set(k, [...(other.get(k) ?? []), b]);
    }
  }
  const first = (g: BookGroup) => Math.min(...g.books.map((b) => b.published ?? 99999));
  const groups = [...series.values()].map((g) => ({ ...g, books: [...g.books].sort(inSeriesOrder) }));
  groups.sort((a, b) => first(a) - first(b) || a.title.localeCompare(b.title));
  for (const [k, title] of LITERARY_GROUPS) {
    const list = other.get(k);
    if (list?.length) groups.push({ key: `literary:${k || 'other'}`, kind: 'literary', title, books: [...list].sort((a, b) => byYear(b, a) || titleKey(a.title).localeCompare(titleKey(b.title))) });
  }
  return groups;
}

/** A series' books in series order. */
export const seriesBooks = (books: PageBook[]) => [...books].sort(inSeriesOrder);

/** "Book 3", or the series' own label ("Book 0.5", "Prequel"). */
export function placeText(b: Pick<PageBook, 'seriesPos' | 'seriesLabel'>): string | null {
  if (b.seriesLabel) return b.seriesLabel;
  return b.seriesPos !== null ? `Book ${fmtPos(b.seriesPos)}` : null;
}
const fmtPos = (n: number) => (Number.isInteger(n) ? String(n) : String(n));

/** "6", "6 and 9", "2, 6 and 9". */
function andList(items: string[]): string {
  if (items.length < 2) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}

/**
 * The whole numbers missing from your list, up to the highest you have: "Missing from your list:
 * Book 6" (null when none are missing). Past eight, the rest are counted.
 */
export function missingText(positions: (number | null)[]): string | null {
  const have = new Set(positions.filter((p): p is number => p !== null && Number.isInteger(p)));
  const max = Math.max(0, ...positions.filter((p): p is number => p !== null).map(Math.floor));
  const gaps: number[] = [];
  for (let n = 1; n <= max; n++) if (!have.has(n)) gaps.push(n);
  if (!gaps.length) return null;
  const shown = gaps.slice(0, 8).map(String);
  const more = gaps.length - shown.length;
  const list = more ? `${shown.join(', ')} and ${more} more` : andList(shown);
  return `Missing from your list: ${gaps.length === 1 ? 'Book' : 'Books'} ${list}`;
}

/**
 * Books of one series out of series order in Future reads: the first place where a higher-numbered
 * book comes before a lower one (null when they're in order). `early` is the one placed too soon.
 */
export function outOfOrder(books: PageBook[]): { early: PageBook; late: PageBook; books: PageBook[] } | null {
  const listed = books.filter((b) => b.status === 'future_reads' && b.rank !== null && b.seriesPos !== null).sort((a, b) => a.rank! - b.rank!);
  for (let i = 1; i < listed.length; i++) {
    const a = listed[i - 1]!;
    const b = listed[i]!;
    if (a.seriesPos! > b.seriesPos!) return { early: a, late: b, books: listed };
  }
  return null;
}

/** On an author's page: "In Future reads, The Leopard (Book 8) comes before The Snowman (Book 7)." */
export function outOfOrderLine(o: { early: PageBook; late: PageBook }): string {
  return `In Future reads, ${o.early.title} (${placeText(o.early)}) comes before ${o.late.title} (${placeText(o.late)}).`;
}

/** On the series page: what Put them in series order does. */
export function outOfOrderNote(o: { early: PageBook; late: PageBook }): string {
  return `${o.early.title} (${placeText(o.early)}) is at #${o.early.rank}, before ${o.late.title} (${placeText(o.late)}) at #${o.late.rank}. Putting them in series order swaps them within the places they already hold, so nothing else in Future reads moves.`;
}

const DONE = new Set(['finished', 'currently_reading', 'not_for_me', 'reference']);

/**
 * Next in the series for you: after the book you're reading (the highest-numbered one), or else the
 * last one you finished, the first numbered book you haven't read or set aside. Null when you
 * haven't started the series, or there's nothing after.
 */
export function nextInSeries(books: PageBook[]): { book: PageBook; after: PageBook; how: 'reading' | 'finished' } | null {
  const numbered = seriesBooks(books).filter((b) => b.seriesPos !== null);
  const top = (list: PageBook[]) => list.reduce<PageBook | null>((m, b) => (m === null || b.seriesPos! > m.seriesPos! ? b : m), null);
  const reading = top(numbered.filter((b) => b.status === 'currently_reading'));
  const after = reading ?? top(numbered.filter((b) => b.status === 'finished'));
  if (!after) return null;
  const book = numbered.find((b) => b.seriesPos! > after.seriesPos! && !DONE.has(b.status ?? ''));
  return book ? { book, after, how: reading ? 'reading' : 'finished' } : null;
}

/** The card's line: "Book 4 · after The Redbreast, which you’re reading (27%) · Future reads · Kindle". */
export function nextLine(n: { book: PageBook; after: PageBook; how: 'reading' | 'finished' }, percent?: number | null): string {
  const after = n.how === 'reading' ? `after ${n.after.title}, which you’re reading${percent !== null && percent !== undefined ? ` (${Math.round(percent)}%)` : ''}` : `after ${n.after.title}, which you finished`;
  return [placeText(n.book), after, shelfText(n.book), copyText(n.book) ?? 'Not owned yet'].filter(Boolean).join(' · ');
}

/** The Shelf column: the shelf's name, and the place on Up Next ("Up Next · #3"; only Up Next shows it). */
export function shelfText(b: Pick<PageBook, 'status' | 'rank'>): string {
  const name = b.status ? statusName(b.status) : 'No shelf yet';
  return b.status === 'up_next' && b.rank ? `${name} · #${b.rank}` : name;
}

/** The counts under the name: "9 books on your list · 1 finished · 1 reading". */
export function countsLine(books: PageBook[]): string {
  const n = books.length;
  const fin = books.filter((b) => b.status === 'finished').length;
  const reading = books.filter((b) => b.status === 'currently_reading');
  return [
    `${n} ${n === 1 ? 'book' : 'books'} on your list`,
    fin ? `${fin} finished` : null,
    reading.length === 1 && reading[0]!.seriesPos !== null && books.every((b) => b.seriesId === reading[0]!.seriesId) ? `reading ${placeText(reading[0]!)}` : reading.length ? `${reading.length} reading` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

// ---------- sorting ----------

export type PeopleSortKey = 'num' | 'title' | 'published' | 'shelf' | 'copy' | 'read';
export interface PeopleSort {
  key: PeopleSortKey;
  desc?: boolean;
}

export const PEOPLE_SORTS: [PeopleSortKey, string][] = [
  ['num', 'Series order'],
  ['title', 'Title'],
  ['published', 'Published'],
  ['shelf', 'Shelf'],
  ['copy', 'Copy'],
  ['read', 'Your read'],
];

const SHELF_ORDER = ['currently_reading', 'up_next', 'future_reads', 'finished', 'not_for_me', 'reference'];
const shelfRank = (b: PageBook) => {
  const i = SHELF_ORDER.indexOf(b.status ?? '');
  return (i < 0 ? SHELF_ORDER.length : i) * 100000 + (b.rank ?? 0);
};

/** A group's books sorted by a header; ties keep series order (then newest first). */
export function sortBooks(books: PageBook[], sort: PeopleSort): PageBook[] {
  const base = [...books].sort((a, b) => inSeriesOrder(a, b) || byYear(b, a));
  const empty = (s: string | null) => (s ? s : '￿');
  const cmp: Record<PeopleSortKey, (a: PageBook, b: PageBook) => number> = {
    num: inSeriesOrder,
    title: (a, b) => titleKey(a.title).localeCompare(titleKey(b.title)),
    published: byYear,
    shelf: (a, b) => shelfRank(a) - shelfRank(b),
    copy: (a, b) => empty(copyText(a)).localeCompare(empty(copyText(b))),
    read: (a, b) => empty(a.read).localeCompare(empty(b.read)),
  };
  const out = base.sort(cmp[sort.key]);
  return sort.desc ? out.reverse() : out;
}

/** A group's own order: series order for a series, newest first otherwise. */
export const defaultSort = (g: Pick<BookGroup, 'kind'>): PeopleSort => (g.kind === 'series' ? { key: 'num' } : { key: 'published', desc: true });
